import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { mockRestaurantSales } from "@/mocks/restaurantSales";
import { mockStaffList } from "@/mocks/restaurantData";
import * as XLSX from "xlsx";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

const PASSWORD = "Enc256Enc";

interface SaleItem {
  product_id: string;
  quantity: number;
  unit_price: number;
  description: string;
}

interface RestaurantSale {
  id: string;
  payment_method: string;
  total_amount: number;
  created_at: string;
  room_number: string;
  customer_name: string;
  sale_type: string;
  staff_id: string | null;
  collected_by_staff_id: string | null;
  collected_at: string | null;
  payment_status: string;
  sale_items: SaleItem[];
}

interface StaffMember {
  id: string;
  fullName: string;
  role: string;
  phone: string;
  isActive: boolean;
}

interface DealerUserAccount {
  id: string;
  fullName: string;
  role: string;
}

interface PendingRoomGroup {
  key: string;
  roomLabel: string;
  orders: RestaurantSale[];
  total: number;
  saleIds: string[];
  latestCreatedAt: string;
}

interface EditableItem {
  product_id: string;
  description: string;
  quantity: number;
  unit_price: number;
}

type FilterKey = "all" | "nakit" | "pos" | "cari" | "kismi_odeme";

const filters: { key: FilterKey; label: string; icon: string; color: string; count?: number }[] = [
  { key: "all", label: "Tümü", icon: "ri-list-unordered", color: "bg-foreground-700" },
  { key: "nakit", label: "Nakit", icon: "ri-money-dollar-circle-line", color: "bg-primary-500" },
  { key: "pos", label: "Kredi Kartı", icon: "ri-bank-card-line", color: "bg-accent-500" },
  { key: "cari", label: "Oda Hesabı", icon: "ri-hotel-bed-line", color: "bg-secondary-500" },
  { key: "kismi_odeme", label: "Kısmi Ödeme", icon: "ri-refund-2-line", color: "bg-foreground-600" },
];

const paymentLabels: Record<string, string> = {
  nakit: "Nakit",
  pos: "Kredi Kartı",
  cari: "Oda Hesabı",
  iban: "Havale/EFT",
  kismi_odeme: "Kısmi Ödeme",
};

const paymentColors: Record<string, string> = {
  nakit: "bg-primary-100 text-primary-700",
  pos: "bg-accent-100 text-accent-700",
  cari: "bg-secondary-100 text-secondary-700",
  iban: "bg-emerald-100 text-emerald-700",
  kismi_odeme: "bg-foreground-100 text-foreground-700",
};

const paymentIcons: Record<string, string> = {
  nakit: "ri-money-dollar-circle-line",
  pos: "ri-bank-card-line",
  cari: "ri-hotel-bed-line",
  iban: "ri-bank-line",
  kismi_odeme: "ri-refund-2-line",
};

interface Product {
  id: string;
  name: string;
  price: number;
}

export default function RestaurantSalesReportPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId;
  const [sales, setSales] = useState<RestaurantSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingRawSales, setPendingRawSales] = useState<RestaurantSale[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [expandedSale, setExpandedSale] = useState<string | null>(null);

  const [editingSaleId, setEditingSaleId] = useState<string | null>(null);
  const [editItems, setEditItems] = useState<EditableItem[]>([]);
  const [editRoomNumber, setEditRoomNumber] = useState("");
  const [editCustomerName, setEditCustomerName] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState<string>("nakit");
  const [editInfoError, setEditInfoError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [sstPasswordModal, setSstPasswordModal] = useState(false);
  const [sstPasswordInput, setSstPasswordInput] = useState("");
  const [sstPasswordError, setSstPasswordError] = useState("");
  const SST_PASSWORD = "At151015&";

  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [dealerUsers, setDealerUsers] = useState<DealerUserAccount[]>([]);
  const [dealerAdminIds, setDealerAdminIds] = useState<string[]>([]);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [collectingSaleIds, setCollectingSaleIds] = useState<string[]>([]);
  const [collectingRoomLabel, setCollectingRoomLabel] = useState("");
  const [collectingMethod, setCollectingMethod] = useState<"nakit" | "pos" | null>(null);
  const [collectingError, setCollectingError] = useState("");
  const [collecting, setCollecting] = useState(false);
  const [expandedRoomKey, setExpandedRoomKey] = useState<string | null>(null);
  const [transferModal, setTransferModal] = useState(false);
  const [transferSourceGroup, setTransferSourceGroup] = useState<PendingRoomGroup | null>(null);
  const [transferTargetRoom, setTransferTargetRoom] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [activeTab, setActiveTab] = useState<"sales" | "pending">("sales");

  // Kısmi ödeme modal
  const [partialModal, setPartialModal] = useState(false);
  const [partialSaleId, setPartialSaleId] = useState<string | null>(null);
  const [partialSaleLabel, setPartialSaleLabel] = useState("");
  const [partialAmount, setPartialAmount] = useState("");
  const [partialMethod, setPartialMethod] = useState<"nakit" | "pos">("nakit");
  const [partialError, setPartialError] = useState("");
  const [savingPartial, setSavingPartial] = useState(false);

  // Bekleyen sipariş düzenleme (şifreli)
  const [pendingEditSaleId, setPendingEditSaleId] = useState<string | null>(null);

  // Password protection for edit/delete
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [pendingAction, setPendingAction] = useState<"edit" | "delete" | null>(null);
  const [pendingSaleId, setPendingSaleId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState<string>(todayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [activeDatePreset, setActiveDatePreset] = useState<string>("today");

  const datePresets = [
    { key: "today", label: "Bugün", getRange: () => {
      const t = new Date();
      const ds = t.toISOString().slice(0, 10);
      return { s: ds, e: ds };
    }},
    { key: "yesterday", label: "Dün", getRange: () => {
      const t = new Date(); t.setDate(t.getDate() - 1);
      const ds = t.toISOString().slice(0, 10);
      return { s: ds, e: ds };
    }},
    { key: "last7", label: "Son 7 Gün", getRange: () => {
      const t = new Date();
      const e = t.toISOString().slice(0, 10);
      t.setDate(t.getDate() - 6);
      const s = t.toISOString().slice(0, 10);
      return { s, e };
    }},
    { key: "thisMonth", label: "Bu Ay", getRange: () => {
      const t = new Date();
      const e = t.toISOString().slice(0, 10);
      const s = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-01`;
      return { s, e };
    }},
    { key: "lastMonth", label: "Geçen Ay", getRange: () => {
      const t = new Date();
      t.setMonth(t.getMonth() - 1);
      const ys = t.getFullYear();
      const ms = t.getMonth();
      const s = `${ys}-${String(ms + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(ys, ms + 1, 0).getDate();
      const e = `${ys}-${String(ms + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { s, e };
    }},
  ];

  const applyDatePreset = (preset: typeof datePresets[0]) => {
    setActiveDatePreset(preset.key);
    const range = preset.getRange();
    setStartDate(range.s);
    setEndDate(range.e);
  };

  const getDateOnly = (dateStr: string) => {
    return dateStr.slice(0, 10);
  };

  const normalizeRoomKey = (room: string) => {
    const trimmed = (room || "").trim().toLocaleUpperCase("tr-TR").replace(/\s+/g, " ");
    // Strip leading zeros from any numeric segment so "B1", "B01" and "B010" are treated as the same room
    return trimmed.replace(/\d+/g, (digits) => String(parseInt(digits, 10)));
  };

  const fetchSales = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");

    try {
      const supabase = getSupabase();
      const { data: salesData, error: salesErr } = await supabase
        .from("sales")
        .select("id, payment_method, total_amount, created_at, room_number, customer_name, sale_type, staff_id, collected_by_staff_id, collected_at, payment_status")
        .eq("dealer_id", dealerId)
        .eq("sale_type", "restaurant")
        .order("created_at", { ascending: false })
        .limit(100);

      if (salesErr) throw salesErr;

      if (!salesData || salesData.length === 0) {
        setSales(mockRestaurantSales as RestaurantSale[]);
        setLoading(false);
        return;
      }

      const salesWithItems = await Promise.all(
        salesData.map(async (sale: any) => {
          const { data: items, error: itemsErr } = await supabase
            .from("sale_items")
            .select("product_id, quantity, unit_price, description")
            .eq("sale_id", sale.id);

          return {
            ...sale,
            sale_items: itemsErr || !items ? [] : items,
          };
        })
      );

      setSales(salesWithItems);
    } catch (e: any) {
      setError(e.message || "Satış verileri yüklenirken hata oluştu.");
      setSales(mockRestaurantSales as RestaurantSale[]);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  const fetchPendingSales = useCallback(async () => {
    if (!dealerId) {
      setPendingLoading(false);
      return;
    }
    setPendingLoading(true);
    try {
      const supabase = getSupabase();
      const { data: salesData, error: salesErr } = await supabase
        .from("sales")
        .select("id, payment_method, total_amount, created_at, room_number, customer_name, sale_type, staff_id, collected_by_staff_id, collected_at, payment_status")
        .eq("dealer_id", dealerId)
        .eq("sale_type", "restaurant")
        .eq("payment_status", "pending")
        .in("payment_method", ["cari", "kismi_odeme"])
        .order("created_at", { ascending: false });

      if (salesErr) throw salesErr;

      if (!salesData || salesData.length === 0) {
        setPendingRawSales([]);
        setPendingLoading(false);
        return;
      }

      const salesWithItems = await Promise.all(
        salesData.map(async (sale: any) => {
          const { data: items, error: itemsErr } = await supabase
            .from("sale_items")
            .select("product_id, quantity, unit_price, description")
            .eq("sale_id", sale.id);

          return {
            ...sale,
            sale_items: itemsErr || !items ? [] : items,
          };
        })
      );

      setPendingRawSales(salesWithItems);
    } catch {
      setPendingRawSales([]);
    } finally {
      setPendingLoading(false);
    }
  }, [dealerId]);

  const fetchProducts = useCallback(async () => {
    if (!dealerId) return;
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("products")
        .select("id, name, price")
        .eq("dealer_id", dealerId)
        .neq("product_code", "REST-001")
        .order("name");

      if (err) throw err;
      setProducts(data || []);
    } catch {
      setProducts([]);
    }
  }, [dealerId]);

  const fetchStaff = useCallback(async () => {
    if (!dealerId) {
      setStaffList(mockStaffList as StaffMember[]);
      return;
    }
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("staff")
        .select("*")
        .eq("dealer_id", dealerId);

      if (err) throw err;
      if (data && data.length > 0) {
        setStaffList(
          (data as Array<Record<string, unknown>>).map((s) => ({
            id: s.id as string,
            fullName: (s.full_name as string) || "",
            role: (s.role as string) || "",
            phone: (s.phone as string) || "",
            isActive: (s.is_active as boolean) ?? true,
          }))
        );
      } else {
        setStaffList(mockStaffList as StaffMember[]);
      }
    } catch {
      setStaffList(mockStaffList as StaffMember[]);
    }
  }, [dealerId]);

  const fetchDealerPersonnel = useCallback(async () => {
    if (!dealerId) {
      setDealerUsers([]);
      setDealerAdminIds([]);
      return;
    }
    try {
      const supabase = getSupabase();
      const [usersRes, adminsRes] = await Promise.all([
        supabase.from("dealer_users").select("id, full_name, role").eq("dealer_id", dealerId),
        supabase.from("dealer_admins").select("id").eq("dealer_id", dealerId),
      ]);

      if (usersRes.data) {
        setDealerUsers(
          (usersRes.data as Array<Record<string, unknown>>).map((u) => ({
            id: u.id as string,
            fullName: (u.full_name as string) || "",
            role: (u.role as string) || "",
          }))
        );
      } else {
        setDealerUsers([]);
      }

      if (adminsRes.data) {
        setDealerAdminIds((adminsRes.data as Array<Record<string, unknown>>).map((a) => a.id as string));
      } else {
        setDealerAdminIds([]);
      }
    } catch {
      setDealerUsers([]);
      setDealerAdminIds([]);
    }
  }, [dealerId]);

  const openCollectModal = (group: PendingRoomGroup) => {
    setCollectingSaleIds(group.saleIds);
    setCollectingRoomLabel(group.roomLabel);
    setCollectingMethod(null);
    setCollectingError("");
    setShowCollectModal(true);
  };

  const closeCollectModal = () => {
    setShowCollectModal(false);
    setCollectingSaleIds([]);
    setCollectingRoomLabel("");
    setCollectingMethod(null);
    setCollectingError("");
  };

  const openTransferModal = (group: PendingRoomGroup) => {
    setTransferSourceGroup(group);
    setTransferTargetRoom("");
    setTransferError("");
    setTransferModal(true);
  };

  const transferRoom = async () => {
    if (!transferSourceGroup) return;
    const rawTarget = transferTargetRoom.trim();
    if (!rawTarget) {
      setTransferError("Lütfen hedef oda adını girin!");
      return;
    }
    const targetKey = normalizeRoomKey(rawTarget);
    if (targetKey === transferSourceGroup.key) {
      setTransferError("Hedef oda kaynak odayla aynı olamaz!");
      return;
    }
    // Check if target room already has pending sales — they'll be merged automatically
    // since normalizeRoomKey groups them. Just update room_number on all source sales.
    setTransferring(true);
    setTransferError("");
    try {
      const sourceIds = transferSourceGroup.saleIds;
      if (dealerId) {
        const supabase = getSupabase();
        const { error } = await supabase
          .from("sales")
          .update({ room_number: rawTarget })
          .in("id", sourceIds);
        if (error) throw error;
      }
      // Update local state
      const updater = (list: RestaurantSale[]) =>
        list.map((s) =>
          sourceIds.includes(s.id) ? { ...s, room_number: rawTarget } : s
        );
      setSales(updater);
      setPendingRawSales(updater);

      const staffName = user?.fullName || user?.username || user?.dealerName || "";
      if (dealerId) {
        logAction(
          dealerId,
          user?.id || null,
          LOG_ACTIONS.SALE_EDIT,
          `Oda Hesabı Aktarımı: Oda ${transferSourceGroup.roomLabel} → Oda ${rawTarget} · ${sourceIds.length} sipariş · Personel: ${staffName}`
        );
      }
      setTransferModal(false);
      setExpandedRoomKey(null);
    } catch (e: any) {
      setTransferError(e.message || "Aktarım sırasında hata oluştu.");
    } finally {
      setTransferring(false);
    }
  };

  const collectPayment = async () => {
    if (collectingSaleIds.length === 0) return;
    if (!collectingMethod) {
      setCollectingError("Lütfen ödemenin nakit mi kartla mı alındığını seçin!");
      return;
    }
    if (!user?.id) {
      setCollectingError("Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.");
      return;
    }
    setCollectingError("");
    setCollecting(true);
    const collectorId = user.id;
    const collectedIds = collectingSaleIds;
    const collectedAt = new Date().toISOString();
    const settledMethod = collectingMethod;

    const applyLocalUpdate = () => {
      const updater = (list: RestaurantSale[]) =>
        list.map((s) =>
          collectedIds.includes(s.id)
            ? {
                ...s,
                collected_by_staff_id: collectorId,
                collected_at: collectedAt,
                payment_status: "completed",
                payment_method: settledMethod,
              }
            : s
        );
      setSales(updater);
      setPendingRawSales((prev) => prev.filter((s) => !collectedIds.includes(s.id)));
    };

    if (!dealerId) {
      applyLocalUpdate();
      closeCollectModal();
      setCollecting(false);
      return;
    }

    try {
      const supabase = getSupabase();
      const { error } = await supabase
        .from("sales")
        .update({
          collected_by_staff_id: collectorId,
          collected_at: collectedAt,
          payment_status: "completed",
          payment_method: settledMethod,
        })
        .in("id", collectedIds);

      if (error) throw error;

      const groupTotal = collectedIds.reduce((sum, id) => {
        const s = sales.find((x) => x.id === id) || pendingRawSales.find((x) => x.id === id);
        return sum + (s ? Number(s.total_amount) : 0);
      }, 0);
      applyLocalUpdate();
      closeCollectModal();
      const staffName = user?.fullName || user?.username || user?.dealerName || "";
      logAction(
        dealerId,
        user?.id || null,
        LOG_ACTIONS.PAYMENT_COLLECT,
        `Tahsilat · Oda:${collectingRoomLabel} · ${collectedIds.length} sipariş · ${paymentLabels[settledMethod]} · Personel:${staffName} · ₺${groupTotal.toFixed(2)}`
      );
    } catch (e: any) {
      setCollectingError(e.message || "Tahsilat kaydedilirken hata oluştu.");
    } finally {
      setCollecting(false);
    }
  };

  useEffect(() => {
    fetchSales();
    fetchPendingSales();
    fetchProducts();
    fetchStaff();
    fetchDealerPersonnel();
  }, [fetchSales, fetchPendingSales, fetchProducts, fetchStaff, fetchDealerPersonnel]);

  const filteredSales = useMemo(() => {
    let result = sales;
    if (activeFilter !== "all") {
      result = result.filter((s) => s.payment_method === activeFilter);
    }
    if (startDate) {
      result = result.filter((s) => getDateOnly(s.created_at) >= startDate);
    }
    if (endDate) {
      result = result.filter((s) => getDateOnly(s.created_at) <= endDate);
    }
    return result;
  }, [sales, activeFilter, startDate, endDate]);

  const dateFilteredSales = useMemo(() => {
    let result = sales;
    if (startDate) {
      result = result.filter((s) => getDateOnly(s.created_at) >= startDate);
    }
    if (endDate) {
      result = result.filter((s) => getDateOnly(s.created_at) <= endDate);
    }
    return result;
  }, [sales, startDate, endDate]);

  const stats = useMemo(() => {
    const s = dateFilteredSales;
    const total = s.reduce((sum, sale) => sum + Number(sale.total_amount), 0);
    const nakit = s.filter((sale) => sale.payment_method === "nakit").reduce((sum, sale) => sum + Number(sale.total_amount), 0);
    const pos = s.filter((sale) => sale.payment_method === "pos").reduce((sum, sale) => sum + Number(sale.total_amount), 0);
    const cari = s.filter((sale) => sale.payment_method === "cari").reduce((sum, sale) => sum + Number(sale.total_amount), 0);
    const kismi = s.filter((sale) => sale.payment_method === "kismi_odeme").reduce((sum, sale) => sum + Number(sale.total_amount), 0);
    return { total, nakit, pos, cari, kismi };
  }, [dateFilteredSales]);

  const paymentCounts = useMemo(() => {
    const s = dateFilteredSales;
    const nakit = s.filter((sale) => sale.payment_method === "nakit").length;
    const pos = s.filter((sale) => sale.payment_method === "pos").length;
    const cari = s.filter((sale) => sale.payment_method === "cari").length;
    const kismi = s.filter((sale) => sale.payment_method === "kismi_odeme").length;
    return { nakit, pos, cari, kismi };
  }, [dateFilteredSales]);

  const pendingSales = useMemo(() => {
    return pendingRawSales.filter(
      (s) =>
        (s.payment_method === "cari" || s.payment_method === "kismi_odeme") &&
        s.payment_status === "pending"
    );
  }, [pendingRawSales]);

  const pendingTotal = useMemo(() => {
    return pendingSales.reduce((sum, s) => sum + Number(s.total_amount), 0);
  }, [pendingSales]);

  const pendingRoomGroups = useMemo<PendingRoomGroup[]>(() => {
    const map = new Map<string, PendingRoomGroup>();
    for (const s of pendingSales) {
      const key = normalizeRoomKey(s.room_number) || `__no_room__${s.id}`;
      const label = normalizeRoomKey(s.room_number) || (s.room_number || "-");
      const existing = map.get(key);
      if (existing) {
        existing.orders.push(s);
        existing.total += Number(s.total_amount);
        existing.saleIds.push(s.id);
        if (new Date(s.created_at) > new Date(existing.latestCreatedAt)) {
          existing.latestCreatedAt = s.created_at;
        }
      } else {
        map.set(key, {
          key,
          roomLabel: label,
          orders: [s],
          total: Number(s.total_amount),
          saleIds: [s.id],
          latestCreatedAt: s.created_at,
        });
      }
    }
    // Sort orders within each group oldest-first (order they were placed)
    for (const g of map.values()) {
      g.orders.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime()
    );
  }, [pendingSales]);

  const getStaffName = (staffId: string | null): string => {
    if (!staffId) return "-";
    const dealerUser = dealerUsers.find((d) => d.id === staffId);
    if (dealerUser) return dealerUser.fullName || "-";
    if (dealerAdminIds.includes(staffId)) return "Bayi Admin";
    // Legacy fallback: sales recorded before the manual staff-selection step was removed
    const staff = staffList.find((s) => s.id === staffId);
    return staff ? staff.fullName : "-";
  };

  const actingStaffName = user?.fullName || user?.username || user?.dealerName || "Bilinmiyor";
  const actingStaffRole = user?.role || (user?.loginType === "dealer" ? "Bayi Yetkilisi" : "");

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products.slice(0, 20);
    const q = productSearch.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 20);
  }, [products, productSearch]);

  const toggleExpand = (id: string) => {
    if (editingSaleId) return;
    setExpandedSale(expandedSale === id ? null : id);
  };

  const expandIcon = (saleId: string) =>
    expandedSale === saleId ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";

  const openEdit = (sale: RestaurantSale) => {
    setExpandedSale(null);
    setEditingSaleId(sale.id);
    setShowProductDropdown(false);
    setProductSearch("");
    setEditRoomNumber(sale.room_number || "");
    setEditCustomerName(sale.customer_name || "");
    setEditPaymentMethod(sale.payment_method);
    setEditInfoError("");
    setEditItems(
      sale.sale_items.map((item) => ({
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit_price: Number(item.unit_price),
      }))
    );
  };

  const handleSecureAction = (action: "edit" | "delete", saleId: string) => {
    setPendingAction(action);
    setPendingSaleId(saleId);
    setPasswordInput("");
    setPasswordError("");
    setPasswordModalOpen(true);
  };

  const confirmPasswordAction = () => {
    if (passwordInput !== PASSWORD) {
      setPasswordError("Geçersiz şifre! Lütfen tekrar deneyin.");
      return;
    }
    setPasswordModalOpen(false);
    setPasswordInput("");
    setPasswordError("");

    if (pendingAction === "edit" && pendingSaleId) {
      const sale = sales.find((s) => s.id === pendingSaleId);
      if (sale) openEdit(sale);
    } else if (pendingAction === "edit" && pendingEditSaleId) {
      // Edit from pending tab
      const sale = pendingRawSales.find((s) => s.id === pendingEditSaleId) || sales.find((s) => s.id === pendingEditSaleId);
      if (sale) openEdit(sale);
      setPendingEditSaleId(null);
    } else if (pendingAction === "delete" && pendingSaleId) {
      deleteSale(pendingSaleId);
    }
  };

  const deleteSale = async (saleId: string) => {
    if (!dealerId) return;
    setDeleting(true);
    try {
      const supabase = getSupabase();
      await supabase.from("sale_items").delete().eq("sale_id", saleId);
      const { error } = await supabase.from("sales").delete().eq("id", saleId);
      if (error) throw error;
      setSales((prev) => prev.filter((s) => s.id !== saleId));
      const sale = sales.find((s) => s.id === saleId);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.SALE_DELETE, sale ? `Satış silindi · Oda:${sale.room_number} · Müşteri:${sale.customer_name} · ₺${Number(sale.total_amount).toFixed(2)}` : "Satış silindi");
    } catch (e: any) {
      alert(e.message || "Satış silinirken hata oluştu.");
    } finally {
      setDeleting(false);
    }
  };

  const cleanupFakeSales = async () => {
    if (!dealerId) return;
    setCleaningUp(true);
    setSstPasswordModal(false);
    try {
      const supabase = getSupabase();

      // Find sale_items with description 'Restoran Satışı'
      const { data: fakeItems } = await supabase
        .from("sale_items")
        .select("sale_id, description")
        .eq("description", "Restoran Satışı");

      if (!fakeItems || fakeItems.length === 0) {
        alert("Silinecek sahte satış bulunamadı.");
        setCleaningUp(false);
        return;
      }

      // Group by sale_id - only delete sales where ALL items are fake
      const fakeSaleIds = [...new Set((fakeItems as Array<{ sale_id: string }>).map((i) => i.sale_id))];

      // For each candidate sale, verify ALL items are 'Restoran Satışı'
      const salesToDelete: string[] = [];
      for (const saleId of fakeSaleIds) {
        const { data: allItems } = await supabase
          .from("sale_items")
          .select("description")
          .eq("sale_id", saleId);
        const allFake = allItems && allItems.every((i: any) => i.description === "Restoran Satışı");
        // Also only delete if it belongs to this dealer
        const { data: saleRow } = await supabase
          .from("sales")
          .select("id")
          .eq("id", saleId)
          .eq("dealer_id", dealerId)
          .maybeSingle();
        if (allFake && saleRow) salesToDelete.push(saleId);
      }

      if (salesToDelete.length === 0) {
        alert("Bu bayiye ait silinecek sahte satış bulunamadı.");
        setCleaningUp(false);
        return;
      }

      await supabase.from("sale_items").delete().in("sale_id", salesToDelete);
      await supabase.from("sales").delete().in("id", salesToDelete);

      setSales((prev) => prev.filter((s) => !salesToDelete.includes(s.id)));
      setPendingRawSales((prev) => prev.filter((s) => !salesToDelete.includes(s.id)));

      logAction(dealerId, user?.id || null, LOG_ACTIONS.SALE_DELETE, `${salesToDelete.length} adet sahte "Restoran Satışı" kaydı temizlendi`);
      alert(`${salesToDelete.length} adet sahte "Restoran Satışı" kaydı başarıyla silindi.`);
    } catch (e: any) {
      alert("Temizleme sırasında hata: " + (e.message || "Bilinmeyen hata"));
    } finally {
      setCleaningUp(false);
    }
  };

  const openPartialModal = (saleId: string, customerName: string, amount: number, groupSaleIds?: string[]) => {
    setPartialSaleId(saleId);
    setPartialSaleLabel(`${customerName} · ${formatCurrency(amount)}`);
    setPartialAmount("");
    setPartialMethod("nakit");
    setPartialError("");
    setPartialModal(true);
  };

  const savePartialPayment = async () => {
    const amount = parseFloat(partialAmount);
    if (!partialAmount || isNaN(amount) || amount <= 0) {
      setPartialError("Lütfen geçerli bir tutar girin!");
      return;
    }
    if (!partialSaleId || !dealerId) return;
    setSavingPartial(true);
    setPartialError("");
    try {
      const supabase = getSupabase();
      // Get original sale info
      const origSale = pendingRawSales.find((s) => s.id === partialSaleId) || sales.find((s) => s.id === partialSaleId);
      const origAmount = Number(origSale?.total_amount || 0);
      const remaining = origAmount - amount;

      // Update original sale: reduce total by partial amount
      await supabase.from("sales").update({
        total_amount: Math.max(0, remaining),
        payment_status: remaining <= 0 ? "completed" : "pending",
        collected_by_staff_id: user?.id || null,
        collected_at: remaining <= 0 ? new Date().toISOString() : null,
      }).eq("id", partialSaleId);

      // Create a new completed sale record for the partial payment
      if (amount > 0) {
        const { data: newSale } = await supabase.from("sales").insert({
          dealer_id: dealerId,
          room_number: origSale?.room_number || null,
          customer_name: origSale?.customer_name || null,
          payment_method: partialMethod,
          total_amount: amount,
          payment_status: "completed",
          sale_type: "restaurant",
          staff_id: user?.id || null,
          collected_by_staff_id: user?.id || null,
          collected_at: new Date().toISOString(),
        }).select("id").single();

        // Copy items proportionally (descriptive note)
        if (newSale) {
          await supabase.from("sale_items").insert({
            sale_id: (newSale as any).id,
            product_id: null,
            quantity: 1,
            unit_price: amount,
            description: `Kısmi Ödeme - ${origSale?.customer_name || ""}`,
          });
        }
      }

      // Update local state
      if (remaining <= 0) {
        setPendingRawSales((prev) => prev.filter((s) => s.id !== partialSaleId));
      } else {
        const updater = (list: RestaurantSale[]) =>
          list.map((s) => s.id === partialSaleId ? { ...s, total_amount: remaining } : s);
        setPendingRawSales(updater);
        setSales(updater);
      }

      logAction(dealerId, user?.id || null, LOG_ACTIONS.PAYMENT_COLLECT,
        `Kısmi Ödeme · ${origSale?.room_number ? `Oda:${origSale.room_number} · ` : ""}₺${amount.toFixed(2)} ${partialMethod === "nakit" ? "Nakit" : "Kart"}`);
      setPartialModal(false);
      fetchPendingSales();
    } catch (e: any) {
      setPartialError(e.message || "Kaydedilirken hata oluştu.");
    } finally {
      setSavingPartial(false);
    }
  };

  const openPendingOrderEdit = (saleId: string) => {
    setPendingEditSaleId(saleId);
    setPendingAction("edit");
    setPasswordInput("");
    setPasswordError("");
    setPasswordModalOpen(true);
  };

  const closeEdit = () => {
    setEditingSaleId(null);
    setEditItems([]);
    setEditRoomNumber("");
    setEditCustomerName("");
    setEditPaymentMethod("nakit");
    setEditInfoError("");
    setProductSearch("");
    setShowProductDropdown(false);
  };

  const editTotal = useMemo(() => {
    return editItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  }, [editItems]);

  const updateItem = (index: number, field: keyof EditableItem, value: string | number) => {
    setEditItems((prev) => {
      const next = [...prev];
      const val = field === "quantity" ? Math.max(1, Number(value) || 1) : field === "unit_price" ? Math.max(0, Number(value) || 0) : value;
      next[index] = { ...next[index], [field]: val };
      return next;
    });
  };

  const removeItem = (index: number) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  };

  const addProduct = (product: Product) => {
    setEditItems((prev) => [
      ...prev,
      {
        product_id: product.id,
        description: product.name,
        quantity: 1,
        unit_price: Number(product.price) || 0,
      },
    ]);
    setProductSearch("");
    setShowProductDropdown(false);
  };

  const addCustomItem = () => {
    setEditItems((prev) => [
      ...prev,
      {
        product_id: crypto.randomUUID(),
        description: "",
        quantity: 1,
        unit_price: 0,
      },
    ]);
  };

  const saveEdit = async () => {
    if (!editingSaleId || !dealerId) return;

    const invalid = editItems.some((item) => !item.description.trim() || item.quantity < 1);
    if (invalid) {
      alert("Tüm ürünlerin açıklaması ve en az 1 adet olması zorunludur.");
      return;
    }
    if (!editRoomNumber.trim() || !editCustomerName.trim()) {
      setEditInfoError("Oda Numarası ve Müşteri Adı alanları boş bırakılamaz!");
      return;
    }
    setEditInfoError("");

    setSaving(true);
    try {
      const supabase = getSupabase();
      const newTotal = editItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
      const newPaymentStatus = (editPaymentMethod === "cari" || editPaymentMethod === "kismi_odeme") ? "pending" : "completed";

      await supabase.from("sale_items").delete().eq("sale_id", editingSaleId);

      const insertItems = editItems.map((item) => ({
        sale_id: editingSaleId,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        description: item.description,
      }));
      const { error: insertErr } = await supabase.from("sale_items").insert(insertItems);
      if (insertErr) throw insertErr;

      const { error: updateErr } = await supabase
        .from("sales")
        .update({
          total_amount: newTotal,
          room_number: editRoomNumber.trim(),
          customer_name: editCustomerName.trim(),
          payment_method: editPaymentMethod,
          payment_status: newPaymentStatus,
        })
        .eq("id", editingSaleId);
      if (updateErr) throw updateErr;

      setSales((prev) =>
        prev.map((s) => {
          if (s.id === editingSaleId) {
            return {
              ...s,
              total_amount: newTotal,
              room_number: editRoomNumber.trim(),
              customer_name: editCustomerName.trim(),
              payment_method: editPaymentMethod,
              payment_status: newPaymentStatus,
              sale_items: editItems.map((item) => ({
                product_id: item.product_id,
                quantity: item.quantity,
                unit_price: item.unit_price,
                description: item.description,
              })),
            };
          }
          return s;
        })
      );

      closeEdit();
      logAction(dealerId, user?.id || null, LOG_ACTIONS.SALE_UPDATE, `Satış düzenlendi · Oda:${editRoomNumber.trim()} · ${paymentLabels[editPaymentMethod] || editPaymentMethod} · ${editItems.length} kalem · ₺${newTotal.toFixed(2)}`);
      // Keep the pending-payments tab in sync since payment method/status may have changed
      fetchPendingSales();
    } catch (e: any) {
      alert(e.message || "Değişiklikler kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const exportToExcel = () => {
    const exportSales = filteredSales.length > 0 ? filteredSales : sales;

    const dateRangeLabel = startDate && endDate
      ? `${startDate} - ${endDate}`
      : startDate
        ? `${startDate} ve sonrası`
        : endDate
          ? `${endDate} ve öncesi`
          : "Tüm Zamanlar";

    const wb = XLSX.utils.book_new();

    const summaryData = [
      ["RESTORAN SATIŞ RAPORU - ÖZET"],
      ["Tarih Aralığı:", dateRangeLabel],
      ["Oluşturma Tarihi:", new Date().toLocaleDateString("tr-TR")],
      [""],
      ["Ödeme Yöntemi", "Satış Adedi", "Toplam Tutar (₺)", "Ortalama Tutar (₺)"],
    ];

    const summaryMethods = [
      { key: "nakit", label: "Nakit" },
      { key: "pos", label: "Kredi Kartı" },
      { key: "cari", label: "Oda Hesabı" },
      { key: "kismi_odeme", label: "Kısmi Ödeme" },
    ];

    summaryMethods.forEach((method) => {
      const methodSales = exportSales.filter((s) => s.payment_method === method.key);
      const count = methodSales.length;
      const total = methodSales.reduce((sum, s) => sum + Number(s.total_amount), 0);
      const avg = count > 0 ? total / count : 0;
      summaryData.push([method.label, count, total, Math.round(avg * 100) / 100]);
    });

    const grandTotal = exportSales.reduce((sum, s) => sum + Number(s.total_amount), 0);
    const grandCount = exportSales.length;
    const grandAvg = grandCount > 0 ? grandTotal / grandCount : 0;
    summaryData.push(["GENEL TOPLAM", grandCount, grandTotal, Math.round(grandAvg * 100) / 100]);

    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    ws1["!cols"] = [{ wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 18 }];

    const boldStyle = { font: { bold: true } };
    ws1.A1.s = boldStyle;
    XLSX.utils.book_append_sheet(wb, ws1, "Satış Özeti");

    const detailHeader = [
      "Oda No", "Müşteri", "Tarih", "Saat", "Ödeme Yöntemi", "Ürün", "Miktar", "Birim Fiyat (₺)", "Satır Toplam (₺)", "Satış Toplam (₺)",
    ];
    const detailData: (string | number)[][] = [detailHeader];

    exportSales.forEach((sale) => {
      const dateOnly = getDateOnly(sale.created_at);
      const timeOnly = new Date(sale.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
      const saleTotal = Number(sale.total_amount);

      if (sale.sale_items.length === 0) {
        detailData.push([
          sale.room_number || "",
          sale.customer_name || "",
          dateOnly,
          timeOnly,
          paymentLabels[sale.payment_method] || sale.payment_method,
          "(Ürün yok)",
          0,
          0,
          0,
          saleTotal,
        ]);
      } else {
        sale.sale_items.forEach((item) => {
          detailData.push([
            sale.room_number || "",
            sale.customer_name || "",
            dateOnly,
            timeOnly,
            paymentLabels[sale.payment_method] || sale.payment_method,
            item.description,
            item.quantity,
            Number(item.unit_price),
            item.quantity * Number(item.unit_price),
            saleTotal,
          ]);
        });
      }
    });

    const ws2 = XLSX.utils.aoa_to_sheet(detailData);
    ws2["!cols"] = [
      { wch: 8 }, { wch: 22 }, { wch: 12 }, { wch: 8 }, { wch: 14 },
      { wch: 30 }, { wch: 8 }, { wch: 16 }, { wch: 18 }, { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Satış Detayları");

    const paymentSummaryHeader = [
      "Ödeme Yöntemi", "Satış No", "Oda", "Müşteri", "Tarih", "Kalem Sayısı", "Tutar (₺)",
    ];
    const psData: (string | number)[][] = [paymentSummaryHeader];

    exportSales.forEach((sale, idx) => {
      psData.push([
        paymentLabels[sale.payment_method] || sale.payment_method,
        idx + 1,
        sale.room_number || "",
        sale.customer_name || "",
        getDateOnly(sale.created_at),
        sale.sale_items.length,
        Number(sale.total_amount),
      ]);
    });

    const ws3 = XLSX.utils.aoa_to_sheet(psData);
    ws3["!cols"] = [
      { wch: 16 }, { wch: 10 }, { wch: 8 }, { wch: 22 }, { wch: 12 }, { wch: 14 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, ws3, "Ödeme Özeti");

    const filename = `restoran_satis_raporu_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  if (!user?.hasRestaurantReports) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mb-4">
          <i className="ri-lock-line text-3xl text-accent-500"></i>
        </div>
        <h4 className="text-lg font-semibold text-foreground-950 mb-1">Erişim Kısıtlandı</h4>
        <p className="text-sm text-foreground-500 mb-4 text-center max-w-sm">
          Bu sayfaya erişmek için bayi yöneticinizden restoran modülü yetkisi talep edin.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Dashboard'a Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-restaurant-line text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">Restoran Satış Raporları</h1>
          </div>
          <p className="text-sm text-foreground-500 mt-0.5">
            Restoran modülünden yapılan tüm satışları ve açık oda hesaplarını görüntüleyin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setSstPasswordInput(""); setSstPasswordError(""); setSstPasswordModal(true); }}
            disabled={cleaningUp}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            title="Sahte Satışları Temizle"
          >
            <i className={`ri-delete-bin-line ${cleaningUp ? "animate-spin" : ""}`}></i>
            {cleaningUp ? "Temizleniyor..." : "SST"}
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line"></i>
            Excel İndir
          </button>
          <button
            onClick={fetchSales}
            className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Yenile
          </button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <p className="text-xs font-medium text-foreground-500 flex items-center gap-1.5 shrink-0">
            <i className="ri-calendar-line text-sm"></i>
            Tarih Filtresi:
          </p>

          <div className="flex flex-wrap items-center gap-1.5">
            {datePresets.map((preset) => (
              <button
                key={preset.key}
                onClick={() => applyDatePreset(preset)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeDatePreset === preset.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200/70"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto shrink-0">
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-2-line text-sm text-foreground-400"></i>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setActiveDatePreset(""); }}
                className="px-2 py-1 bg-background-50 border border-background-200/70 rounded-lg text-xs text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 cursor-pointer"
              />
            </div>
            <span className="text-xs text-foreground-400">-</span>
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-check-line text-sm text-foreground-400"></i>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setActiveDatePreset(""); }}
                className="px-2 py-1 bg-background-50 border border-background-200/70 rounded-lg text-xs text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 cursor-pointer"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => applyDatePreset(datePresets[0])}
                className="w-6 h-6 rounded-full flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-50 cursor-pointer"
                title="Filtreyi temizle"
              >
                <i className="ri-close-circle-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Toplam Ciro</p>
          <p className="text-lg font-bold text-foreground-950">{formatCurrency(stats.total)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{dateFilteredSales.length} satış</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xs text-primary-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Nakit</p>
          </div>
          <p className="text-lg font-bold text-foreground-950">{formatCurrency(stats.nakit)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{paymentCounts.nakit} satış</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-bank-card-line text-xs text-accent-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Kredi Kartı</p>
          </div>
          <p className="text-lg font-bold text-foreground-950">{formatCurrency(stats.pos)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{paymentCounts.pos} satış</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-secondary-100 flex items-center justify-center">
              <i className="ri-hotel-bed-line text-xs text-secondary-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Oda Hesabı</p>
          </div>
          <p className="text-lg font-bold text-secondary-600">{formatCurrency(stats.cari)}</p>
          <p className="text-xs text-foreground-400 mt-0.5">{paymentCounts.cari} bekleyen</p>
        </div>
      </div>

      {/* Payment Filter Buttons */}
      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => {
          const isActive = activeFilter === f.key;
          const count = f.key === "all"
            ? dateFilteredSales.length
            : f.key === "nakit"
              ? paymentCounts.nakit
              : f.key === "pos"
                ? paymentCounts.pos
                : f.key === "cari"
                  ? paymentCounts.cari
                  : paymentCounts.kismi;

          return (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive
                  ? `${f.color} text-background-50`
                  : "bg-background-50 border border-background-200/70 text-foreground-500 hover:bg-background-100"
              }`}
            >
              <i className={`${f.icon} text-sm`}></i>
              <span>{f.label}</span>
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
                isActive ? "bg-background-50/20 text-background-50" : "bg-background-100 text-foreground-400"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchSales} className="ml-auto text-accent-700 font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        <button
          onClick={() => setActiveTab("sales")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeTab === "sales"
              ? "bg-background-50 text-foreground-950"
              : "text-foreground-500 hover:text-foreground-700"
          }`}
        >
          <i className="ri-file-list-3-line mr-1.5"></i>
          Tüm Satışlar
        </button>
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap relative ${
            activeTab === "pending"
              ? "bg-background-50 text-foreground-950"
              : "text-foreground-500 hover:text-foreground-700"
          }`}
        >
          <i className="ri-time-line mr-1.5"></i>
          Bekleyen Ödemeler
          {pendingRoomGroups.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
              {pendingRoomGroups.length}
            </span>
          )}
        </button>
      </div>

      {/* Pending Payments Tab */}
      {activeTab === "pending" && (
        <>
          {pendingSales.length > 0 && (
            <div className="bg-secondary-50 border border-secondary-200/70 rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-secondary-700">
                  <i className="ri-information-line mr-1"></i>
                  Bekleyen Tahsilat · Tüm Tarihler
                </p>
                <p className="text-sm font-bold text-secondary-700">
                  Toplam: {formatCurrency(pendingTotal)}
                </p>
              </div>
              <p className="text-xs text-secondary-600">
                Aynı odaya ait tahsil edilmemiş siparişler tek hesapta toplanır. Detayları görmek için odaya tıklayın, tahsilat yapıldığında odadaki tüm siparişler birlikte kapanır.
              </p>
            </div>
          )}

          {pendingLoading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
            </div>
          ) : pendingRoomGroups.length === 0 ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-check-double-line text-2xl text-emerald-500"></i>
              </div>
              <p className="text-foreground-500 font-medium">Bekleyen ödeme bulunmuyor</p>
              <p className="text-xs text-foreground-400 mt-1">Tüm oda hesapları ve kısmi ödemeler tahsil edilmiş</p>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-xl divide-y divide-background-200/70 overflow-hidden">
              {pendingRoomGroups.map((group) => {
                const isExpanded = expandedRoomKey === group.key;
                return (
                  <div key={group.key}>
                    <button
                      onClick={() => setExpandedRoomKey(isExpanded ? null : group.key)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-background-100/50 transition-colors cursor-pointer text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                          <i className="ri-hotel-bed-line text-rose-600"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground-950">Oda {group.roomLabel}</span>
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-foreground-100 text-foreground-600 whitespace-nowrap">
                              {group.orders.length} sipariş
                            </span>
                          </div>
                          <p className="text-xs text-foreground-400 mt-0.5">
                            Son işlem: {formatDate(group.latestCreatedAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-base font-bold text-foreground-950">{formatCurrency(group.total)}</span>
                        <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}></i>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4">
                        <div className="bg-background-100 rounded-xl divide-y divide-background-200/70 mb-3">
                          {group.orders.map((order) => (
                            <div key={order.id} className="p-3">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-900">
                                  <i className="ri-user-line text-xs text-foreground-400"></i>
                                  {order.customer_name || "-"}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${paymentColors[order.payment_method] || "bg-foreground-100 text-foreground-600"}`}>
                                    {paymentLabels[order.payment_method] || order.payment_method}
                                  </span>
                                  <span className="text-sm font-semibold text-foreground-950">
                                    {formatCurrency(Number(order.total_amount))}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openPendingOrderEdit(order.id); }}
                                    className="w-6 h-6 flex items-center justify-center rounded text-foreground-400 hover:bg-primary-100 hover:text-primary-600 cursor-pointer transition-colors"
                                    title="Siparişi Düzenle (Şifreli)"
                                  >
                                    <i className="ri-pencil-line text-xs"></i>
                                  </button>
                                </div>
                              </div>
                              {order.sale_items && order.sale_items.length > 0 && (
                                <ul className="text-xs text-foreground-500 space-y-0.5 mb-1.5">
                                  {order.sale_items.map((item, idx) => (
                                    <li key={idx} className="flex items-center justify-between">
                                      <span>{item.quantity} adet {item.description || "Ürün"}</span>
                                      <span>{formatCurrency(Number(item.unit_price) * Number(item.quantity))}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <div className="flex items-center justify-between mt-1.5">
                                <p className="text-[11px] text-foreground-400 flex items-center gap-3">
                                  <span>{formatDate(order.created_at)}</span>
                                  <span className="flex items-center gap-1">
                                    <i className="ri-user-star-line"></i>
                                    {getStaffName(order.staff_id)}
                                  </span>
                                </p>
                                <button
                                  onClick={(e) => { e.stopPropagation(); openPartialModal(order.id, order.customer_name || "", Number(order.total_amount)); }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-wallet-3-line text-xs"></i>
                                  Kısmi Ödeme Al
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openTransferModal(group)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-secondary-100 hover:bg-secondary-200/70 text-secondary-700 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-swap-box-line"></i>
                            Oda Hesabı Aktar
                          </button>
                          <button
                            onClick={() => {
                              const firstOrder = group.orders[0];
                              if (firstOrder) openPartialModal(firstOrder.id, `Oda ${group.roomLabel}`, group.total);
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-wallet-3-line"></i>
                            Kısmi Ödeme Al
                          </button>
                          <button
                            onClick={() => openCollectModal(group)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-money-dollar-circle-line"></i>
                            Tamamını Tahsil Et ({formatCurrency(group.total)})
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Sales Tab */}
      {activeTab === "sales" && (
        <>

      {/* Sales Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : filteredSales.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-restaurant-2-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-foreground-500 font-medium">Gösterilecek satış bulunamadı</p>
          <p className="text-xs text-foreground-400 mt-1">Seçili filtreye uygun satış kaydı mevcut değil</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          {/* Desktop Table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm min-w-[850px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Oda</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Müşteri</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ödeme Yönt.</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Personel</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Detay</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center w-12"></th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSales.map((sale) => (
                  <>
                    <tr
                      key={sale.id}
                      className={`border-t border-background-200/70 transition-colors ${
                        editingSaleId === sale.id ? "bg-secondary-50/50" : "hover:bg-background-100/50 cursor-pointer"
                      }`}
                      onClick={() => editingSaleId !== sale.id && toggleExpand(sale.id)}
                    >
                      <td className="px-4 py-3">
                        <div className={`w-6 h-6 rounded-full ${paymentColors[sale.payment_method].split(" ")[0]} flex items-center justify-center`}>
                          <i className={`${paymentIcons[sale.payment_method] || "ri-check-line"} text-xs ${paymentColors[sale.payment_method].split(" ")[1]}`}></i>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-door-line text-foreground-400 text-xs"></i>
                          <span className="font-medium text-foreground-900">{sale.room_number || "-"}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-900">{sale.customer_name || "-"}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{formatDate(sale.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${paymentColors[sale.payment_method] || "bg-foreground-100 text-foreground-600"}`}>
                          {paymentLabels[sale.payment_method] || sale.payment_method}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs space-y-0.5">
                          <div className="flex items-center gap-1 text-foreground-600">
                            <i className="ri-user-line text-[10px]"></i>
                            <span>{getStaffName(sale.staff_id)}</span>
                          </div>
                          {(sale.payment_method === "cari" || sale.payment_method === "kismi_odeme") && sale.collected_by_staff_id && (
                            <div className="flex items-center gap-1 text-emerald-600">
                              <i className="ri-money-dollar-circle-line text-[10px]"></i>
                              <span>{getStaffName(sale.collected_by_staff_id)}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-950">
                        {formatCurrency(Number(sale.total_amount))}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs text-foreground-400">{sale.sale_items.length} kalem</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSecureAction("edit", sale.id); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                          title="Satışı düzenle"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center w-12">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSecureAction("delete", sale.id); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Satışı sil"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </td>
                    </tr>
                    {expandedSale === sale.id && editingSaleId !== sale.id && (
                      <tr key={`${sale.id}-items`} className="bg-background-100/50">
                        <td colSpan={10} className="px-4 py-3">
                          <div className="text-sm space-y-1.5">
                            <p className="text-xs font-medium text-foreground-400 mb-2">SİPARİŞ DETAYLARI</p>
                            {sale.sale_items.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1.5 border-b border-background-200/50 last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className="w-5 h-5 rounded bg-foreground-100 flex items-center justify-center text-xs text-foreground-500 font-medium">
                                    {item.quantity}x
                                  </span>
                                  <span className="text-foreground-700">{item.description}</span>
                                </div>
                                <span className="text-foreground-600 font-medium text-xs">
                                  {formatCurrency(Number(item.unit_price))}
                                </span>
                              </div>
                            ))}
                            <div className="flex items-center justify-between py-1.5 pt-2 border-t border-background-200/70">
                              <span className="text-xs font-semibold text-foreground-700">Toplam</span>
                              <span className="text-sm font-bold text-foreground-950">{formatCurrency(Number(sale.total_amount))}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-background-200/70">
            {filteredSales.map((sale) => (
              <div key={sale.id}>
                <div
                  className={`p-4 ${editingSaleId === sale.id ? "bg-secondary-50/50" : "cursor-pointer"}`}
                  onClick={() => editingSaleId !== sale.id && toggleExpand(sale.id)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full ${paymentColors[sale.payment_method].split(" ")[0]} flex items-center justify-center`}>
                        <i className={`${paymentIcons[sale.payment_method] || "ri-check-line"} text-xs ${paymentColors[sale.payment_method].split(" ")[1]}`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-door-line text-foreground-400 text-xs"></i>
                          <span className="font-medium text-foreground-900 text-sm">{sale.room_number || "-"}</span>
                        </div>
                        <p className="text-xs text-foreground-500 mt-0.5">{sale.customer_name || "-"}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground-950 text-sm">{formatCurrency(Number(sale.total_amount))}</p>
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium mt-0.5 ${paymentColors[sale.payment_method] || "bg-foreground-100 text-foreground-600"}`}>
                        {paymentLabels[sale.payment_method] || sale.payment_method}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-400">{formatDate(sale.created_at)}</span>
                      <span className="text-xs text-foreground-400">· {sale.sale_items.length} kalem</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSecureAction("edit", sale.id); }}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                        title="Satışı düzenle"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSecureAction("delete", sale.id); }}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="Satışı sil"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                      <i className={`${expandIcon(sale.id)} text-foreground-400 text-sm`}></i>
                    </div>
                  </div>
                </div>
                {expandedSale === sale.id && editingSaleId !== sale.id && (
                  <div className="px-4 pb-4 border-t border-background-200/50 bg-background-100/50">
                    <div className="pt-3 space-y-1.5">
                      <p className="text-xs font-medium text-foreground-400 mb-2">SİPARİŞ DETAYLARI</p>
                      {sale.sale_items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1.5 border-b border-background-200/50 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded bg-foreground-100 flex items-center justify-center text-xs text-foreground-500 font-medium">
                              {item.quantity}x
                            </span>
                            <span className="text-sm text-foreground-700">{item.description}</span>
                          </div>
                          <span className="text-foreground-600 font-medium text-xs">{formatCurrency(Number(item.unit_price))}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between py-1.5 pt-2 border-t border-background-200/70">
                        <span className="text-xs font-semibold text-foreground-700">Toplam</span>
                        <span className="text-sm font-bold text-foreground-950">{formatCurrency(Number(sale.total_amount))}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* Edit Slide-over Panel */}
      {editingSaleId && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={closeEdit}></div>
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-background-50 shadow-lg flex flex-col animate-slide-in-right">
            {/* Panel Header */}
            <div className="h-14 flex items-center justify-between px-4 border-b border-background-200/70 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                  <i className="ri-edit-line text-secondary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">Satış Düzenle</p>
                  <p className="text-xs text-foreground-400">
                    {editRoomNumber ? `Oda ${editRoomNumber}` : ""}
                    {editCustomerName ? ` · ${editCustomerName}` : ""}
                  </p>
                </div>
              </div>
              <button onClick={closeEdit} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Panel Body - Editable Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider">Satış Bilgileri</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Oda Numarası</label>
                  <input
                    type="text"
                    value={editRoomNumber}
                    onChange={(e) => { setEditRoomNumber(e.target.value); setEditInfoError(""); }}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Müşteri Adı</label>
                  <input
                    type="text"
                    value={editCustomerName}
                    onChange={(e) => { setEditCustomerName(e.target.value); setEditInfoError(""); }}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Ödeme Türü</label>
                <div className="grid grid-cols-2 gap-2">
                  {(["nakit", "pos", "cari", "kismi_odeme"] as const).map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setEditPaymentMethod(method)}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                        editPaymentMethod === method
                          ? "bg-secondary-100 border-secondary-300 text-secondary-700"
                          : "bg-background-100 border-background-200/70 text-foreground-600 hover:bg-background-200/70"
                      }`}
                    >
                      <i className={paymentIcons[method]}></i>
                      {paymentLabels[method]}
                    </button>
                  ))}
                </div>
                {(editPaymentMethod === "cari" || editPaymentMethod === "kismi_odeme") && (
                  <p className="text-[11px] text-secondary-600 mt-1.5">
                    <i className="ri-information-line mr-1"></i>
                    Bu tür seçilirse satış "Bekleyen Ödemeler" listesine düşer.
                  </p>
                )}
              </div>

              {editInfoError && (
                <div className="p-3 bg-accent-100 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {editInfoError}
                </div>
              )}

              <div className="border-t border-background-200/70 pt-3">
                <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider">Sipariş Kalemleri</p>
              </div>

              {editItems.map((item, index) => (
                <div key={index} className="bg-background-100 border border-background-200/70 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        placeholder="Ürün adı"
                        className="w-full px-2 py-1.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      />
                    </div>
                    <button
                      onClick={() => removeItem(index)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-300 hover:text-accent-500 hover:bg-accent-50 cursor-pointer shrink-0 mt-0.5"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                      <button
                        onClick={() => updateItem(index, "quantity", Math.max(1, item.quantity - 1))}
                        className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer text-sm"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", e.target.value)}
                        min="1"
                        className="w-12 h-8 text-center text-sm font-medium text-foreground-950 bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        onClick={() => updateItem(index, "quantity", item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer text-sm"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                      <span className="text-xs text-foreground-400 pr-2">adet</span>
                    </div>
                    <div className="flex items-center bg-background-50 border border-background-200/70 rounded-lg overflow-hidden flex-1">
                      <span className="text-xs text-foreground-400 pl-2 pr-1">₺</span>
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                        min="0"
                        step="0.01"
                        className="flex-1 h-8 text-sm font-medium text-foreground-950 bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="text-xs text-foreground-400 pr-2">birim</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-medium text-foreground-600">
                      Ara Toplam: {formatCurrency(item.quantity * item.unit_price)}
                    </span>
                  </div>
                </div>
              ))}

              {/* Add Product Section */}
              <div className="border-t border-background-200/70 pt-3 space-y-2">
                <p className="text-xs font-medium text-foreground-400">Ürün Ekle</p>

                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-400"></i>
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                        onFocus={() => setShowProductDropdown(true)}
                        placeholder="Ürün ara..."
                        className="w-full pl-8 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      />
                    </div>
                    <button
                      onClick={addCustomItem}
                      className="flex items-center gap-1 px-3 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i> Boş Kalem
                    </button>
                  </div>

                  {showProductDropdown && productSearch.trim() && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowProductDropdown(false)}></div>
                      <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-background-50 border border-background-200/70 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {filteredProducts.length === 0 ? (
                          <p className="px-3 py-4 text-xs text-foreground-400 text-center">Ürün bulunamadı</p>
                        ) : (
                          filteredProducts.map((p) => (
                            <button
                              key={p.id}
                              onClick={() => addProduct(p)}
                              className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer text-left"
                            >
                              <span className="text-foreground-900">{p.name}</span>
                              <span className="text-xs text-foreground-500">{formatCurrency(Number(p.price))}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Panel Footer */}
            <div className="border-t border-background-200/70 p-4 space-y-3 shrink-0">
              <div className="flex items-center justify-between px-1">
                <span className="text-sm text-foreground-500">Genel Toplam</span>
                <span className="text-lg font-bold text-foreground-950">{formatCurrency(editTotal)}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={closeEdit}
                  className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving || editItems.length === 0}
                  className="flex-1 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Kaydediliyor
                    </span>
                  ) : (
                    "Değişiklikleri Kaydet"
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Payment Collection Modal */}
      {showCollectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={closeCollectModal}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950">
                  <a id="collect-payment">Ödeme Tahsilatı</a>
                </h4>
                <p className="text-xs text-foreground-500 mt-0.5">
                  Tahsilat, oturumu açık olan personel üzerinden kaydedilecek
                </p>
              </div>
              <button onClick={closeCollectModal} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Room Group Info */}
            {(() => {
              const orders = pendingRawSales.filter((s) => collectingSaleIds.includes(s.id));
              if (orders.length === 0) return null;
              const groupTotal = orders.reduce((sum, s) => sum + Number(s.total_amount), 0);
              return (
                <div className="bg-background-100 rounded-xl p-3 mb-4">
                  <div className="text-xs text-foreground-500 space-y-1.5">
                    <div className="flex justify-between">
                      <span>Oda:</span>
                      <span className="font-medium text-foreground-900">{collectingRoomLabel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sipariş Sayısı:</span>
                      <span className="font-medium text-foreground-900">{orders.length}</span>
                    </div>
                    <div className="border-t border-background-200/70 pt-1.5 mt-1.5 space-y-1">
                      {orders.map((o) => (
                        <div key={o.id} className="flex justify-between">
                          <span>{o.customer_name || "-"}</span>
                          <span>{formatCurrency(Number(o.total_amount))}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between border-t border-background-200/70 pt-1.5 mt-1.5">
                      <span className="font-medium text-foreground-700">Tahsil Edilecek Tutar:</span>
                      <span className="font-bold text-foreground-950">{formatCurrency(groupTotal)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Acting staff (session user) */}
            <p className="text-xs font-medium text-foreground-500 mb-2">
              <i className="ri-user-star-line mr-1"></i>
              Ödemeyi Tahsil Eden Personel
            </p>
            <div className="mb-4">
              <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm bg-emerald-100 border border-emerald-200 text-emerald-700">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-emerald-200 text-emerald-700">
                  {actingStaffName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{actingStaffName}</p>
                  {actingStaffRole && <p className="text-xs opacity-70">{actingStaffRole}</p>}
                </div>
                <i className="ri-checkbox-circle-fill text-lg text-emerald-600"></i>
              </div>
            </div>

            {/* Payment method used for collection */}
            <p className="text-xs font-medium text-foreground-500 mb-2">
              <i className="ri-bank-card-2-line mr-1"></i>
              Ödeme Nasıl Alındı? *
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => { setCollectingMethod("nakit"); setCollectingError(""); }}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                  collectingMethod === "nakit"
                    ? "bg-primary-100 border-primary-300 text-primary-700"
                    : "bg-background-100 border-background-200/70 text-foreground-600 hover:bg-background-200/70"
                }`}
              >
                <i className="ri-money-dollar-circle-line text-lg"></i> Nakit
              </button>
              <button
                onClick={() => { setCollectingMethod("pos"); setCollectingError(""); }}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                  collectingMethod === "pos"
                    ? "bg-accent-100 border-accent-300 text-accent-700"
                    : "bg-background-100 border-background-200/70 text-foreground-600 hover:bg-background-200/70"
                }`}
              >
                <i className="ri-bank-card-line text-lg"></i> Kredi Kartı
              </button>
            </div>

            {collectingError && (
              <div className="mb-4 p-3 bg-accent-100 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {collectingError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={closeCollectModal}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={collectPayment}
                disabled={collecting || !collectingMethod}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {collecting ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </span>
                ) : (
                  <>
                    <i className="ri-check-line mr-1"></i> Tahsilatı Onayla
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kısmi Ödeme Modalı */}
      {partialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setPartialModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950">Kısmi Ödeme Al</h4>
                <p className="text-xs text-foreground-400 mt-0.5">{partialSaleLabel}</p>
              </div>
              <button onClick={() => setPartialModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Tahsil Edilecek Tutar (₺)</label>
              <input
                type="number"
                inputMode="decimal"
                value={partialAmount}
                onChange={(e) => { setPartialAmount(e.target.value); setPartialError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") savePartialPayment(); }}
                autoFocus
                placeholder="0.00"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 text-foreground-950"
              />
            </div>

            <p className="text-xs font-medium text-foreground-500 mb-2">Ödeme Yöntemi</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { id: "nakit" as const, label: "Nakit", icon: "ri-money-dollar-circle-line" },
                { id: "pos"   as const, label: "Kredi Kartı", icon: "ri-bank-card-line" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setPartialMethod(opt.id)}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer ${
                    partialMethod === opt.id
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-background-200/70 bg-background-100 text-foreground-600 hover:border-violet-300"
                  }`}
                >
                  <i className={opt.icon}></i> {opt.label}
                </button>
              ))}
            </div>

            {partialError && (
              <div className="mb-4 p-3 bg-accent-50 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i> {partialError}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => setPartialModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium cursor-pointer hover:bg-background-200/70">
                İptal
              </button>
              <button
                onClick={savePartialPayment}
                disabled={savingPartial}
                className="flex-1 py-2.5 bg-violet-500 hover:bg-violet-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50 transition-colors"
              >
                {savingPartial ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin"></i> Kaydediliyor...
                  </span>
                ) : (
                  <><i className="ri-wallet-3-line mr-1"></i> Ödemeyi Kaydet</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Room Transfer Modal */}
      {transferModal && transferSourceGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setTransferModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950">Oda Hesabı Aktarımı</h4>
                <p className="text-xs text-foreground-400 mt-0.5">
                  <span className="font-medium text-foreground-700">Oda {transferSourceGroup.roomLabel}</span> hesabı yeni odaya taşınacak
                </p>
              </div>
              <button onClick={() => setTransferModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Source room summary */}
            <div className="bg-background-100 rounded-xl p-3 mb-4 space-y-1 text-xs text-foreground-500">
              <div className="flex justify-between">
                <span>Kaynak Oda:</span>
                <span className="font-semibold text-foreground-900">Oda {transferSourceGroup.roomLabel}</span>
              </div>
              <div className="flex justify-between">
                <span>Sipariş Sayısı:</span>
                <span className="font-medium text-foreground-900">{transferSourceGroup.orders.length}</span>
              </div>
              <div className="flex justify-between border-t border-background-200/70 pt-1.5 mt-1.5">
                <span>Toplam Tutar:</span>
                <span className="font-bold text-foreground-950">{formatCurrency(transferSourceGroup.total)}</span>
              </div>
            </div>

            {/* Target room input */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Hedef Oda Adı</label>
              <input
                type="text"
                value={transferTargetRoom}
                onChange={(e) => { setTransferTargetRoom(e.target.value); setTransferError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") transferRoom(); }}
                autoFocus
                placeholder="Örn: B3"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-400"
              />
              {transferError && (
                <p className="text-xs text-accent-600 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {transferError}
                </p>
              )}
              <p className="text-[11px] text-foreground-400 mt-1.5">
                Hedef odada zaten açık hesap varsa, bu siparişler mevcut hesabın içine eklenir.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setTransferModal(false)}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={transferRoom}
                disabled={transferring}
                className="flex-1 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Aktarılıyor...
                  </span>
                ) : (
                  <>
                    <i className="ri-swap-box-line mr-1"></i> Aktarımı Onayla
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SST Password Modal */}
      {sstPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setSstPasswordModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950">SST — Güvenlik Doğrulaması</h4>
                <p className="text-xs text-foreground-400 mt-0.5">Sahte satışları temizlemek için şifre gerekli</p>
              </div>
              <button onClick={() => setSstPasswordModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Şifre</label>
              <input
                type="password"
                value={sstPasswordInput}
                onChange={(e) => { setSstPasswordInput(e.target.value); setSstPasswordError(""); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (sstPasswordInput === SST_PASSWORD) cleanupFakeSales();
                    else setSstPasswordError("Geçersiz şifre! Lütfen tekrar deneyin.");
                  }
                }}
                autoFocus
                placeholder="Şifrenizi girin"
                className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              {sstPasswordError && (
                <p className="text-xs text-accent-600 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {sstPasswordError}
                </p>
              )}
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
              <p className="text-xs text-red-700 flex items-start gap-1.5">
                <i className="ri-alert-line shrink-0 mt-0.5"></i>
                <span>Bu işlem, <strong>"Restoran Satışı"</strong> adıyla eklenmiş tüm sahte satışları kalıcı olarak siler. Geri alınamaz.</span>
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setSstPasswordModal(false)}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  if (sstPasswordInput === SST_PASSWORD) {
                    cleanupFakeSales();
                  } else {
                    setSstPasswordError("Geçersiz şifre! Lütfen tekrar deneyin.");
                  }
                }}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line mr-1"></i> Temizle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Protection Modal */}
      {passwordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setPasswordModalOpen(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950">
                  <a id="secure-action-password">Güvenlik Doğrulaması</a>
                </h4>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {pendingAction === "edit" ? "Satış kaydını düzenlemek" : "Satış kaydını silmek"} için şifre gerekli
                </p>
              </div>
              <button
                onClick={() => setPasswordModalOpen(false)}
                className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                  <i className="ri-lock-password-line mr-1"></i>
                  Yetkili Şifresi
                </label>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") confirmPasswordAction(); }}
                  placeholder="Şifreyi girin"
                  autoFocus
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 placeholder:text-foreground-300"
                />
              </div>

              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {passwordError}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setPasswordModalOpen(false)}
                  className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  İptal
                </button>
                <button
                  onClick={confirmPasswordAction}
                  className="flex-1 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shield-check-line mr-1"></i>
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.25s ease-out;
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slideUp 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}